import React from 'react';
import { Link } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Eye, Lock } from 'lucide-react';
import { Button } from '../common/Button';
import { formatCurrency } from '../../utils/calendarHelpers';
import { format } from 'date-fns';
import { isJobLocked } from '../../utils/lockHelpers';
import { InlineDeliveryDateEditor } from './InlineDeliveryDateEditor';
import { InlineDeliveryUserEditor } from './InlineDeliveryUserEditor';
import { InlineJobProductionDateEditor } from './InlineJobProductionDateEditor';
import { isDeliveryRouteJobProductionComplete } from '../../utils/deliveryRouteJobHelpers';

interface DraggableRowProps {
  job: any;
  index: number;
  onViewOrders: (jobId: string, customerName: string) => void;
  onDateUpdated: () => void;
  onUserUpdated: () => void;
  deliveryUsers: any[];
  selectedUsername?: string;
}

export const DraggableRow: React.FC<DraggableRowProps> = ({ 
  job, 
  index,
  onViewOrders,
  onDateUpdated,
  onUserUpdated,
  deliveryUsers,
  selectedUsername
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: job.id });

  const isProductionComplete = isDeliveryRouteJobProductionComplete(job);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging 
      ? '#f3f4f6' 
      : !isProductionComplete 
        ? '#fee2e2'  // Red background for incomplete production (red-100)
        : 'transparent',
  };

  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
    } catch {
      return '-';
    }
  };

  const customerName = job.customer_name || '-';
  const fullAddress = [
    job.delivery_address_line1,
    job.delivery_address_line2
  ].filter(Boolean).join(', ') || '-';
  
  const locked = isJobLocked(job.lock_data);
  const lockReason = job.lock_data?.reason || '';

  // Get status indicator color
  const getStatusColor = () => {
    switch (job.delivery_status) {
      case 'confirmed':
        return 'bg-green-600';
      case 'voicemail':
      case 'voicemail_email':
        return 'bg-orange-600';
      case 'redelivery':
        return 'bg-blue-600';
      default:
        return 'bg-gray-400';
    }
  };

  return (
    <tr 
      ref={setNodeRef} 
      style={style} 
      className={!isProductionComplete ? "hover:bg-red-200" : "hover:bg-gray-50"}
    >
      {/* Drag Handle */}
      <td 
        className="px-2 py-4 text-sm text-gray-400 cursor-move" 
        {...attributes} 
        {...listeners}
      >
        <GripVertical className="w-5 h-5" />
      </td>
      
      {/* Index */}
      <td className="px-3 py-4 text-sm text-gray-900">{index + 1}</td>
      
      {/* Status Indicator */}
      <td className="px-2 py-4">
        <div 
          className={`w-5 h-5 rounded ${getStatusColor()} border border-gray-400`}
          title={job.delivery_status ? job.delivery_status.replace('_', ' ').toUpperCase() : 'No status'}
        />
      </td>
      
      {/* Postcode — link to job edit (same pattern as Jobs list) */}
      <td className="px-3 py-4 text-sm font-medium">
        <Link
          to={`/jobs/${job.id}/edit`}
          className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
        >
          {job.delivery_postcode || '—'}
        </Link>
      </td>
      
      {/* Customer */}
      <td className="px-3 py-4 text-sm text-gray-900">{customerName}</td>
      
      {/* Address */}
      <td className="px-3 py-4 text-sm text-gray-500">{fullAddress}</td>
      
      {/* Price */}
      <td className="px-3 py-4 text-sm text-gray-900">
        {formatCurrency(parseFloat(job.total_amount || '0'))}
      </td>
      
      {/* Quantity */}
      <td className="px-3 py-4 text-sm text-gray-900 text-center font-medium">
        {job.total_items || 0}
      </td>
      
      {/* Glass Arrival */}
      <td className="px-3 py-4 min-w-[148px]">
        <InlineJobProductionDateEditor job={job} field="glass_arrival_date" onUpdated={onDateUpdated} />
      </td>

      {/* Manufacture */}
      <td className="px-3 py-4 min-w-[148px]">
        <InlineJobProductionDateEditor job={job} field="manufacture_date" onUpdated={onDateUpdated} />
      </td>
      
      {/* Delivery Date */}
      <td className="px-3 py-4 min-w-[168px]">
        <InlineDeliveryDateEditor
          job={job}
          onUpdated={onDateUpdated}
        />
      </td>
      
      {/* Engineer - Now editable */}
      <td className="px-3 py-4">
        <InlineDeliveryUserEditor
          job={job}
          deliveryUsers={deliveryUsers}
          onUpdated={onUserUpdated}
          selectedUsername={selectedUsername}
        />
      </td>
      
      {/* View Orders */}
      <td className="px-3 py-4 text-sm">
        <Button
          size="sm"
          variant="secondary"
          icon={<Eye className="w-4 h-4" />}
          onClick={() => onViewOrders(job.id, customerName)}
        >
          View
        </Button>
      </td>
      
      {/* Lock Notes */}
      <td className="px-3 py-4 text-sm text-gray-500">
        {locked ? (
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-yellow-600 flex-shrink-0" />
            <span className="text-xs text-gray-700">{lockReason || 'Locked'}</span>
          </div>
        ) : (
          '-'
        )}
      </td>

      {/* Job Added */}
      <td className="px-3 py-4 text-sm text-gray-500">
        {formatDateTime(job.created_at)}
      </td>
    </tr>
  );
};
